const toposort = require('toposort');

const functionByOperator = {
  Divide: ([l, r]) => l / r,
  Add: list => list.reduce((acc, el) => acc + el, 0),
  Subtract: ([l, r]) => l - r,
  Multiply: list => list.reduce((acc, el) => acc * el, 1),
  Negate: ([l]) => -1 * l,
  Square: ([l]) => Math.pow(l, 2),
  Sqrt: ([l]) => Math.sqrt(l),
  Abs: ([l]) => Math.abs(l),
  Parentheses: ([l]) => l,
  Increment: ([l]) => l + 1,
  Decrement: ([l]) => l - 1,
};

function getCustomMetricKey(customMetric) {
  const { id, parentId } = customMetric;
  return `custom-metric:${parentId ?? id}`;
}

function getCustomMetricValue({ cell, customMetric, resultIndicator }) {
  const { parsedFormula, metrics } = customMetric;
  let value;
  const allMetricsAreNull = metrics.every(metric => cell[metric] == null);
  if (allMetricsAreNull && metrics.length > 0) {
    value = null;
  } else {
    try {
      //   const generatedFunction = getFormulaFunction(parsedFormula);
      //   value = generatedFunction(cell);
      value = calculate(parsedFormula, cell, resultIndicator);
    } catch (error) {
      console.warn('getCustomMetricValue', customMetric, error);
      value = null;
    }
  }
  if (isNaN(value) || value === undefined || value === Infinity) {
    value = null;
  }
  return value;
}

const RESULT_METRICS = new Set(['cvr', 'cpr', 'results']);
function calculate(formula, cell, resultIndicator) {
  if (!Array.isArray(formula)) {
    if (typeof formula === 'object' && formula.num) {
      return parseFloat(formula.num);
    } else if (typeof formula === 'string') {
      const metricKey = formula.substring(1);
      if (metricKey === 'budget') {
        return cell?.budget?.daily;
      } else {
        let value = cell[metricKey];
        if (typeof value === 'string') {
          value = Number(value);
        } else if (typeof value === 'object' && RESULT_METRICS.has(metricKey)) {
          value = Number(value[resultIndicator]);
        }
        return value;
      }
    } else {
      return formula;
    }
  }
  const [operator, ...formulas] = formula;
  const operatorFunction = functionByOperator[operator];
  if (!operatorFunction) {
    console.warn('operatorFunction missing', { operator, formula });
    throw new Error('CUSTOM_METRIC_OPERATOR_NOT_IMPLEMENT');
  }
  let formulaValues = formulas.map(formula => calculate(formula, cell, resultIndicator));
  if (formulaValues.every(value => value == null)) {
    return null;
  } else {
    formulaValues = formulaValues.map(formulaValue => formulaValue || 0);
    return operatorFunction(formulaValues);
  }
}

function getFormulaFunction(formula) {
  if (!Array.isArray(formula)) {
    if (typeof formula === 'object' && formula.num) {
      return () => parseFloat(formula.num);
    } else if (typeof formula === 'string') {
      const metricKey = formula.substring(1);
      if (metricKey === 'budget') {
        return cell => cell?.budget?.daily;
      } else {
        return cell => {
          let value = cell[metricKey];
          if (typeof value === 'string') {
            value = Number(value);
          }
          return value;
        };
      }
    } else {
      return getFormulaFunction(formula);
    }
  }
  const [operator, ...formulas] = formula;
  const operatorFunction = functionByOperator[operator];
  if (!operatorFunction) {
    console.warn('operatorFunction missing', { operator, formula });
    throw new Error('CUSTOM_METRIC_OPERATOR_NOT_IMPLEMENT');
  }
  const formulaSubFunctions = formulas.map(formula => getFormulaFunction(formula));
  const nbOfSubFunctions = formulaSubFunctions.length;
  return cell => {
    let result = formulaSubFunctions[0](cell);
    for (let i = 1; i < nbOfSubFunctions; i++) {
      result = operatorFunction([result, formulaSubFunctions[i](cell) || 0]);
    }
    return result;
  };
}

function addCustomMetrics({ cells, cell, customMetrics, resultIndicator }) {
  if (cells) {
    return addCustomMetricsNew(arguments[0]);
  }
  if (!customMetrics?.length) return;
  customMetrics.forEach(customMetric => {
    const key = getCustomMetricKey(customMetric);
    cell[key] = getCustomMetricValue({ cell, customMetric });
  });
}

function addCustomMetricsNew({ cells, customMetrics, resultIndicator }) {
  if (!customMetrics?.length) return;
  customMetrics.forEach(customMetric => {
    const key = getCustomMetricKey(customMetric);
    cells[key] = getCustomMetricValue({ cell: cells, customMetric, resultIndicator });
  });
}

/**
 * Return the sorted custom metrics, to make sure that
 * if custom metrics c1 formula contains custom metrics c2
 * c1 should be calculated after c2, to it will be after c2
 * in the output of this function.
 */
function sortCustomMetrics(customMetrics) {
  // Build the dependency graph.
  const sortedKeys = getSortedCustomMetricKeys(customMetrics);
  // Get the sorted custom metrics from the sorted keys.
  let customMetricsByKey = {};
  for (const customMetric of customMetrics) {
    customMetricsByKey[getCustomMetricKey(customMetric)] = customMetric;
  }
  let sortedCusomMetrics = [];
  for (let key of sortedKeys) {
    if (customMetricsByKey[key]) sortedCusomMetrics.push(customMetricsByKey[key]);
  }
  return sortedCusomMetrics;
}

function getSortedCustomMetricKeys(customMetrics) {
  const allKeys = [];
  const edges = [];
  for (const customMetric of customMetrics) {
    const dependencies = customMetric.metrics;
    const key = getCustomMetricKey(customMetric);
    allKeys.push(key);
    if (!dependencies) {
      return [];
    }
    for (const dependency of dependencies) {
      // The key should be after its dependency
      if (!dependency.startsWith('custom-metric:')) {
        continue;
      }
      edges.push([dependency, key]);
    }
  }

  try {
    // Do the sorting.
    const sortedKeysWhichHasCustomMetrics = toposort(edges);
    return [
      ...sortedKeysWhichHasCustomMetrics,
      ...allKeys.filter(key => !sortedKeysWhichHasCustomMetrics.includes(key)),
    ];
  } catch (err) {
    // If there is a circular dependency, we cannot compute.
    // TODO: throw a user action required exception
    throw Error('Cusomt metrics contain circular dependcy.');
  }
}

module.exports = {
  addCustomMetrics,
  getCustomMetricValue,
  getCustomMetricKey,
  getFormulaFunction,
  calculate,
  sortCustomMetrics,
  getSortedCustomMetricKeys,
};
